/**
 * B2's five calls, typed from the generated contract (`KLQ-20260819-006`).
 *
 * THREE THINGS THE CONTRACT SAYS THAT THIS FILE IS BUILT AROUND
 *
 * 1. **Neither list is paginated.** `MCK-20260812-006` D names a firm's staff as the small,
 *    stable collection that is the exception; the `Page` envelope is shared so client code
 *    does not fork, but `next_cursor` is always absent. No "load more", no cursor in any key.
 * 2. **A workspace invitation and a project invitation are different resources.**
 *    `GET /workspace/invitations` filters on `scope` server-side, so a project invitation
 *    this firm sent to an external engineer never appears here — which is the one distinction
 *    the two invitation kinds exist to keep.
 * 3. **The invitation response never carries the token**, by design
 *    (`workspaces/schemas.py:WorkspaceInvitationResponse`). Nothing here can build an invite
 *    link, and nothing should try: the link goes to one mailbox.
 *
 * There is no `POST /workspace/members`, and its absence is the product. A person joins a
 * firm by accepting an invitation, never by being inserted — the account has to exist, and
 * the only thing that creates one is somebody proving they hold the mailbox.
 */

import { api, unwrap } from '@/api/client';
import type { components } from '@/api/generated/openapi';

type Schemas = components['schemas'];

export type WorkspaceMember = Schemas['MemberResponse'];
export type WorkspaceMemberPage = Schemas['Page_MemberResponse_'];
export type WorkspaceRole = WorkspaceMember['role'];
export type WorkspaceInvitation = Schemas['WorkspaceInvitationResponse'];
export type WorkspaceInvitationPage = Schemas['Page_WorkspaceInvitationResponse_'];

/**
 * The three workspace roles, strongest first (`core/levels.py:WORKSPACE_ROLES`).
 *
 * TWIN: that tuple. Ordered by authority rather than alphabetically because the drawer's
 * select reads as a ladder, and because `member` — the weakest and the safest — is the
 * default a sender should have to move away from deliberately.
 */
export const WORKSPACE_ROLES: readonly WorkspaceRole[] = ['admin', 'manager', 'member'];

export function listWorkspaceMembers(): Promise<WorkspaceMemberPage> {
  return unwrap(api.GET('/api/v1/workspace/members'));
}

export function listWorkspaceInvitations(): Promise<WorkspaceInvitationPage> {
  return unwrap(api.GET('/api/v1/workspace/invitations'));
}

export function sendWorkspaceInvitation(body: {
  email: string;
  role: WorkspaceRole;
}): Promise<WorkspaceInvitation> {
  return unwrap(api.POST('/api/v1/workspace/invitations', { body }));
}

export function revokeWorkspaceInvitation(invitationId: string): Promise<unknown> {
  return unwrap(
    api.DELETE('/api/v1/workspace/invitations/{invitation_id}', {
      params: { path: { invitation_id: invitationId } },
    }),
  );
}

export function changeWorkspaceMemberRole(
  membershipId: string,
  role: WorkspaceRole,
): Promise<WorkspaceMember> {
  return unwrap(
    api.PATCH('/api/v1/workspace/members/{membership_id}', {
      params: { path: { membership_id: membershipId } },
      body: { role },
    }),
  );
}

export function removeWorkspaceMember(membershipId: string): Promise<unknown> {
  return unwrap(
    api.DELETE('/api/v1/workspace/members/{membership_id}', {
      params: { path: { membership_id: membershipId } },
    }),
  );
}

/**
 * The admin door (`KLQ-20260821-002` §E6), in the shape the routes actually took.
 *
 * **Neither is a sub-path on the member.** `/workspace/members/{id}/password-reset` is a
 * third level of nesting, which `MCK-20260812-006` A does not allow and the route-inventory
 * gate fails the build over — so the reset posts to its own collection with the target in the
 * body, and the password is replaced on an addressable item. Each half keeps the shape its
 * verb wants.
 *
 * **Neither answers with anything.** `202` for the reset — what happened is that a message is
 * on its way, not that a password is different — and `204` for the set. The row the caller is
 * looking at is unchanged by either, which is why nothing here invalidates the member list
 * (`queries.ts`): a password is not a field of `MemberResponse`, and `is_home_account` is the
 * only field on it these two acts could conceivably move, which they cannot.
 */
export function sendMemberPasswordReset(membershipId: string): Promise<unknown> {
  return unwrap(
    api.POST('/api/v1/workspace/member-password-resets', {
      body: { membership_id: membershipId },
    }),
  );
}

export function setMemberPassword(membershipId: string, newPassword: string): Promise<unknown> {
  return unwrap(
    api.PUT('/api/v1/workspace/member-passwords/{membership_id}', {
      params: { path: { membership_id: membershipId } },
      body: { new_password: newPassword },
    }),
  );
}

// --- The studio's firm directory (`KLQ-20260820-007`, owner 2026-09-16) ----------------------
//
// *"Poďme spraviť ten adresár firiem pre štúdio."* A studio works with the same electrician on
// eight projects and typed the name eight times. This is the address book; a project's roster
// row points at a firm here and carries no name of its own.

export type WorkspaceFirm = Schemas['WorkspaceFirmResponse'];
export type WorkspaceFirmContact = Schemas['WorkspaceFirmContactResponse'];
/** A person a *project* wrote down under one of these firms (`KLQ-20260820-007`). */
export type FirmContactOnProject = Schemas['ProjectPartyContactResponse'];

/**
 * The whole directory, **archived firms included**. One list, cached once under the workspace
 * key, and every reader decides what it offers: the directory's *show archived* toggle needs
 * them, and the pickers (task, add party) filter on `archived_at` themselves. Without the flag
 * the server drops archived rows and the toggle had nothing left to show.
 */
export function listWorkspaceFirms(): Promise<WorkspaceFirm[]> {
  return unwrap(
    api.GET('/api/v1/workspace/firms', { params: { query: { include_archived: true } } }),
  );
}

export function listWorkspaceFirmContacts(): Promise<WorkspaceFirmContact[]> {
  return unwrap(api.GET('/api/v1/workspace/firm-contacts', { params: { query: {} } }));
}

/**
 * The people the studio's **projects** wrote down under its firms.
 *
 * Read beside the directory's own contacts, because *"who do I phone at the electrician"* is
 * one question with two answers: the firm's own people belong to the studio, a contact added
 * for one building belongs to that building. Bounded server-side by the caller's projects.
 */
export function listFirmContactsOnProjects(): Promise<FirmContactOnProject[]> {
  return unwrap(api.GET('/api/v1/firm-contacts-on-projects'));
}

export function createWorkspaceFirm(body: {
  readonly name: string;
  readonly note?: string | null;
}): Promise<WorkspaceFirm> {
  return unwrap(api.POST('/api/v1/workspace/firms', { body }));
}

/** Renaming reaches **every project the firm is on** — one firm, one spelling. */
export function updateWorkspaceFirm(
  firmId: string,
  body: {
    readonly name?: string;
    readonly note?: string | null;
    readonly address?: string | null;
    readonly registration_id?: string | null;
    readonly tax_id?: string | null;
    readonly vat_id?: string | null;
    readonly phone?: string | null;
    readonly email?: string | null;
    readonly primary_contact_id?: string | null;
    readonly archived?: boolean;
  },
): Promise<WorkspaceFirm> {
  return unwrap(
    api.PATCH('/api/v1/workspace/firms/{firm_id}', {
      params: { path: { firm_id: firmId } },
      body,
    }),
  );
}

export function createWorkspaceFirmContact(body: {
  readonly firm_id: string;
  readonly name: string;
  readonly phone?: string | null;
  readonly email?: string | null;
}): Promise<WorkspaceFirmContact> {
  return unwrap(api.POST('/api/v1/workspace/firm-contacts', { body }));
}

export function updateWorkspaceFirmContact(
  contactId: string,
  body: {
    readonly name?: string;
    readonly phone?: string | null;
    readonly email?: string | null;
  },
): Promise<WorkspaceFirmContact> {
  return unwrap(
    api.PATCH('/api/v1/workspace/firm-contacts/{contact_id}', {
      params: { path: { contact_id: contactId } },
      body,
    }),
  );
}

export async function deleteWorkspaceFirmContact(contactId: string): Promise<void> {
  await unwrap(
    api.DELETE('/api/v1/workspace/firm-contacts/{contact_id}', {
      params: { path: { contact_id: contactId } },
    }),
  );
}
